/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { Ring } from '@uiball/loaders';
import {
  createMarketPlaceInternal,
  createMarketPlaceExternal,
  getCategory,
  getApprovers,
  checkStore,
  getMyStore,
  editMyStore,
  deleteMyStore,
} from '../../../utils/services/user.service';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import getCountry from '../createstore/country.json';
import config from '@/config/walletConnect';

const getShortNetworkName = (chainId) => {
  if (!chainId) return '';
  const chainIdToShortName = {
    56: 'BEP20',
    97: 'BEP20',
    1: 'ERC20',
    11155111: 'ERC20',
    137: 'POLYGON',
    80001: 'POLYGON',
    123: 'SOLANA',
    321: 'BITCOIN',
    111: 'TRON',
  };
  return chainIdToShortName[chainId] || '';
};

const {
  REACT_APP_SMART_CHAIN,
  REACT_APP_ETHERIUM_CHAIN,
  REACT_APP_POLYGON_MATIC,
} = config;

// Styled Components matching the provided design
const PageContainer = styled.div`
  min-height: 100vh;
  color: white;
  padding: 2.5rem;
  position: relative;
  z-index: 0;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  position: sticky;
  top: 0;
  backdrop-filter: blur(10px);
  z-index: 1;
  padding-bottom: 1rem;
`;

const HeaderTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 600;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
`;

const NewStoreButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  color: black;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(400px);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(400px);
  }
`;

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100%;
  background: #0B1122;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  z-index: 300;
  overflow-y: auto;
  animation: ${props => props.isOpen ? slideIn : slideOut} 0.3s ease;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(400px)'};
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const SidebarTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SelectStyled = styled.select`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  option {
    background: #0B1122;
    color: white;
  }
`;

const ImageGrid = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ImagePreview = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 0.25rem;
`;

const DocumentList = styled.ul`
  font-size: 0.75rem;
  color: #5ce9ff;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  color: white;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  border: none;
  color: black;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const SelectWrapper = styled.div`
  .react-select-container {
    .react-select__control {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.25rem;
      min-height: 42px;
      
      &:hover {
        border-color: rgba(255, 255, 255, 0.2);
      }
    }
    
    .react-select__control--is-focused {
      border-color: #5ce9ff;
      box-shadow: 0 0 0 1px #5ce9ff;
    }
    
    .react-select__single-value,
    .react-select__placeholder {
      color: white;
      font-size: 0.875rem;
    }
    
    .react-select__input-container {
      color: white;
      
      input {
        color: white !important;
      }
    }
    
    .react-select__menu {
      background: #0B1122;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .react-select__option {
      background: #0B1122;
      color: white;
      font-size: 0.875rem;
      
      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
      
      &--is-selected {
        background: #5ce9ff;
        color: black;
      }
    }
    
    .react-select__multi-value {
      background: rgba(92, 233, 255, 0.2);
    }
    
    .react-select__multi-value__label {
      color: white;
    }
  }
`;

const StoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StoreCard = styled.div`
  background: #1a1a1a;
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: default;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }
`;

const StoreImageSection = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #ff6b35;
  position: relative;
`;

const StoreLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StoreContentSection = styled.div`
  background: #1a1a1a;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StoreHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const StoreName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: white;
  flex: 1;
`;

const StoreTag = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  background: #2d2d2d;
  color: white;
  text-transform: capitalize;
`;

const CategoryBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background: rgba(92, 233, 255, 0.15);
  color: #5ce9ff;
  border: 1px solid rgba(92, 233, 255, 0.3);
  text-transform: capitalize;
`;

const StatusBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  text-transform: capitalize;
  background: ${({ status }) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'published':
      case 'active':
        return 'rgba(0, 247, 210, 0.2)';
      case 'pending':
      case 'review':
        return 'rgba(255, 193, 7, 0.2)';
      case 'rejected':
      case 'inactive':
      case 'suspended':
        return 'rgba(248, 113, 113, 0.2)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${({ status }) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'published':
      case 'active':
        return '#00f7d2';
      case 'pending':
      case 'review':
        return '#ffc107';
      case 'rejected':
      case 'inactive':
      case 'suspended':
        return '#f87171';
      default:
        return '#9ca3af';
    }
  }};
  border: 1px solid ${({ status }) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'published':
      case 'active':
        return 'rgba(0, 247, 210, 0.3)';
      case 'pending':
      case 'review':
        return 'rgba(255, 193, 7, 0.3)';
      case 'rejected':
      case 'inactive':
      case 'suspended':
        return 'rgba(248, 113, 113, 0.3)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  }};
`;

const StoreDescription = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
  line-height: 1.5;
`;

const StoreActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  gap: 1rem;
`;

const ViewStoreButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  background: #2d2d2d;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #3d3d3d;
  }
`;

const ActionLinks = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const EditLink = styled.a`
  color: #5ce9ff;
  font-size: 0.875rem;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #00f7d2;
    text-decoration: underline;
  }
`;

const DeleteLink = styled.a`
  color: #ef4444;
  font-size: 0.875rem;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #dc2626;
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #9ca3af;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: white;
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  margin-bottom: 1.5rem;
`;

export default function CreateStoreFlow() {

  const history = useHistory();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [myStores, setMyStores] = useState([]);
  console.log("🚀 ~ CreateStoreFlow ~ myStores:", myStores)
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState(null);

  // Store form data
  const [storeName, setStoreName] = useState('');
  const [slug, setSlug] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [role, setRole] = useState('standard');
  const [status, setStatus] = useState('draft');

  // Additional fields
  const [storeAddress, setStoreAddress] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [selectedType, setSelectedType] = useState('standard');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [onlineDelivery, setOnlineDelivery] = useState(false);
  const [beneficiaryToggle, setBeneficiaryToggle] = useState('internal');
  const [selectedChain, setSelectedChain] = useState(REACT_APP_SMART_CHAIN);
  const [selectedToken, setSelectedToken] = useState(null);

  const { coinList: virtualWalletCoins } = useSelector(state => ({
    coinList: state.coinList1?.data || [],
  }));

  const symbolOptions = useMemo(() => {
    if (!virtualWalletCoins || virtualWalletCoins.length === 0) return [];
    return virtualWalletCoins
      .filter(coin => coin.symbol)
      .map(coin => {
        const chainId = coin.chain_id || coin.chainId || coin.network_name;
        const networkName = getShortNetworkName(chainId);
        const label = networkName ? `${coin.symbol}` : coin.symbol;
        return {
          value: coin.symbol,
          label: label,
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [virtualWalletCoins]);

  useEffect(() => {
    if (symbolOptions.length > 0 && !selectedToken) {
      const usdtOption = symbolOptions.find(opt => opt.value === 'USDT');
      setSelectedToken(usdtOption || symbolOptions[0]);
    }
  }, [symbolOptions, selectedToken]);

  const logoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const imagesInputRef = useRef(null);

  // country.json already has the correct format with value and label
  // Ensure it's a valid array and handle any potential errors
  const countryOptions = useMemo(() => {
    try {
      if (Array.isArray(getCountry) && getCountry.length > 0) {
        // Validate that each item has the correct structure
        return getCountry.filter(item =>
          item && typeof item === 'object' && item.value && item.label
        );
      }
      return [];
    } catch (error) {
      console.error('Error processing country options:', error);
      return [];
    }
  }, []);

  const storeTypeOptions = [
    { value: 'standard', label: 'Standard' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'pharma', label: 'Pharma' },
  ];

  useEffect(() => {
    fetchCategories();
    fetchApprovers();
    fetchMyStores();
  }, []);

  const fetchMyStores = async () => {
    try {
      setIsLoadingStores(true);
      const response = await getMyStore();
      if (response.data?.result) {
        setMyStores(response.data.result);
      }
    } catch (error) {
      console.error('Error fetching my stores:', error);
      setMyStores([])
    } finally {
      setIsLoadingStores(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getCategory();

      if (response.data?.category) {
        const options = response.data.category.map(cat => ({
          value: cat._id,
          label: cat.category_name,
        }));
        setCategories(options);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchApprovers = async () => {
    try {
      const response = await getApprovers();
      if (response.data?.user) {
        const options = response.data.user.map(approver => ({
          value: approver.wallet_address,
          label: approver.name || approver.wallet_address,
        }));
        setApprovers(options);
      }
    } catch (error) {
      console.error('Error fetching approvers:', error);
    }
  };

  // Auto-generate slug from store name
  useEffect(() => {
    if (storeName) {
      const generatedSlug = storeName.toLowerCase().replace(/\s+/g, '-');
      setSlug(generatedSlug);
    }
  }, [storeName]);

  // File validation constants
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_IMAGES = 1;
  const MAX_DOCUMENTS = 1;
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const ALLOWED_DOCUMENT_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

  const validateImageFile = (file) => {
    // Check file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error(`Invalid image type. Allowed types: JPG, JPEG, PNG, GIF, WEBP`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    // Check file size
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Image size exceeds ${MAX_IMAGE_SIZE / (1024 * 1024)}MB limit. File: ${file.name}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    return true;
  };

  const validateDocumentFile = (file) => {
    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['pdf', 'doc', 'docx'];

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(`Invalid document type. Allowed types: PDF, DOC, DOCX. File: ${file.name}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    // Check file size
    if (file.size > MAX_DOCUMENT_SIZE) {
      toast.error(`Document size exceeds ${MAX_DOCUMENT_SIZE / (1024 * 1024)}MB limit. File: ${file.name}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    return true;
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // Check if already has an image
    if (images.length >= MAX_IMAGES) {
      toast.error(`Only ${MAX_IMAGES} image allowed. Please remove the existing image first.`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file
    if (validateImageFile(file)) {
      setImages([URL.createObjectURL(file)]);
      toast.success('Image uploaded successfully', {
        position: toast.POSITION.TOP_RIGHT,
      });
    }

    // Reset input to allow selecting the same file again if needed
    e.target.value = '';
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // Check if already has a document
    if (documents.length >= MAX_DOCUMENTS) {
      toast.error(`Only ${MAX_DOCUMENTS} document allowed. Please remove the existing document first.`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file
    if (validateDocumentFile(file)) {
      setDocuments([{
        name: file.name,
        url: URL.createObjectURL(file),
        file: file,
        hash: null,
        tx: null,
        date: new Date().toISOString(),
        actor: 'user',
        frozen: false,
      }]);
      toast.success('Document uploaded successfully', {
        position: toast.POSITION.TOP_RIGHT,
      });
    }

    // Reset input to allow selecting the same file again if needed
    e.target.value = '';
  };

  const hashFile = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      return Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    } catch (error) {
      console.error('Error hashing file:', error);
      return null;
    }
  };

  const mockOnChainEvent = () => {
    return '0x' + crypto.randomUUID().replace(/-/g, '').slice(0, 64);
  };

  const openNew = () => {
    setIsEditMode(false);
    setEditingStore(null);
    resetForm();
    setSidebarOpen(true);
  };

  const resetForm = () => {
    setStoreName('');
    setSlug('');
    setShortDescription('');
    setImages([]);
    setDocuments([]);
    setStoreAddress('');
    setSelectedCategory(null);
    setSelectedApprover(null);
    setSelectedType('standard');
    setSelectedCountries([]);
    setOnlineDelivery(false);
    setBeneficiaryToggle('internal');
    setStatus('draft');
    if (symbolOptions.length > 0) {
      const usdtOption = symbolOptions.find(opt => opt.value === 'USDT');
      setSelectedToken(usdtOption || symbolOptions[0]);
    } else {
      setSelectedToken(null);
    }
    if (logoInputRef.current) logoInputRef.current.value = '';
    if (documentInputRef.current) documentInputRef.current.value = '';
    if (imagesInputRef.current) imagesInputRef.current.value = '';
  };

  const openEdit = async (store) => {
    try {
      setIsLoading(true);
      setIsEditMode(true);
      setEditingStore(store);

      // Populate form with store data
      setStoreName(store.storeName || '');
      setSlug(store.storeName?.toLowerCase().replace(/\s+/g, '-') || '');
      setShortDescription(store.storeDescription || '');
      setStoreAddress(store.storeAddress || '');
      setSelectedType(store.store_type || 'standard');
      setOnlineDelivery(store.online_delivery_available || false);
      setStatus(store.status || 'draft');

      // Set category
      if (store.store_category_id) {
        const categoryOption = categories.find(
          cat => cat.value === store.store_category_id._id || cat.value === store.store_category_id
        );
        if (categoryOption) {
          setSelectedCategory(categoryOption);
        }
      }

      // Set symbol
      if (store.symbol) {
        const symbolOption = symbolOptions.find(
          sym => sym.value === store.symbol
        );
        if (symbolOption) {
          setSelectedToken(symbolOption);
        } else {
          const usdtOption = symbolOptions.find(opt => opt.value === 'USDT');
          setSelectedToken(usdtOption || symbolOptions[0] || null);
        }
      } else {
        const usdtOption = symbolOptions.find(opt => opt.value === 'USDT');
        setSelectedToken(usdtOption || symbolOptions[0] || null);
      }

      // Set countries
      if (store.delivery_available_countries && Array.isArray(store.delivery_available_countries)) {
        const mappedCountries = store.delivery_available_countries.map(country => {
          if (typeof country === 'string') {
            const found = countryOptions.find(c => c.value === country);
            return found || { value: country, label: country };
          }
          return country;
        }).filter(Boolean);
        setSelectedCountries(mappedCountries);
      }

      // Set images
      if (store.storeLogo) {
        setImages([store.storeLogo]);
      } else {
        setImages([]);
      }

      // Set documents (if available)
      if (store.store_document) {
        setDocuments([{
          name: store.store_document || 'document.pdf',
          url: store.store_document,
          file: null,
          hash: null,
          tx: null,
          date: new Date().toISOString(),
          actor: 'user',
          frozen: false,
        }]);
      } else {
        setDocuments([]);
      }

      setSidebarOpen(true);
    } catch (error) {
      console.error('Error loading store for edit:', error);
      toast.error('Failed to load store data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!storeName) {
      toast.error('Store name is required');
      return;
    }
    if (!shortDescription) {
      toast.error('Description is required');
      return;
    }
    if (!selectedCategory) {
      toast.error('Category is required');
      return;
    }
    // In edit mode, images and documents are optional if they already exist
    if (!isEditMode) {
      if (images.length !== 1) {
        toast.error('Exactly one store image is required');
        return;
      }
      if (documents.length !== 1) {
        toast.error('Exactly one document is required');
        return;
      }
    }
    if (selectedCountries.length === 0) {
      toast.error('At least one delivery country is required');
      return;
    }

    try {
      setIsLoading(true);

      // Get logo (single image) - only if it's a new file
      let logoFile = null;
      if (images.length > 0) {
        const image = images[0];
        // Check if it's a data URL (newly uploaded via createObjectURL)
        if (typeof image === 'string' && image.startsWith('blob:')) {
          // Blob URL from createObjectURL - fetch and convert to File
          try {
            const response = await fetch(image);
            const blob = await response.blob();
            logoFile = new File([blob], 'logo.png', { type: blob.type || 'image/png' });
          } catch (error) {
            console.error('Error converting blob URL to file:', error);
          }
        } else if (typeof image === 'string' && image.startsWith('data:')) {
          // Data URL - convert to File
          try {
            const response = await fetch(image);
            const blob = await response.blob();
            logoFile = new File([blob], 'logo.png', { type: blob.type || 'image/png' });
          } catch (error) {
            console.error('Error converting data URL to file:', error);
          }
        } else if (image instanceof File) {
          // Already a File object
          logoFile = image;
        }
        // If it's an HTTP URL (existing image), logoFile remains null and won't be updated
      }

      // Get document file (single document) - only if it's a new file
      const docFile = documents.length > 0 && documents[0].file ? documents[0].file : null;

      if (isEditMode && editingStore) {
        // Edit mode
        const formData = new FormData();
        formData.append('storeName', storeName);
        formData.append('store_category_id', selectedCategory?.value || '');
        formData.append('storeAddress', storeAddress || '');
        formData.append('storeDescription', shortDescription);
        formData.append('storeId', editingStore._id);
        formData.append('delivery_available_countries', JSON.stringify((selectedCountries || []).map(c => {
          if (typeof c === 'string') return c;
          return c?.value || c;
        })));
        if (logoFile) formData.append('File', logoFile);
        if (docFile) formData.append('File2', docFile);

        const response = await editMyStore(formData);

        if (response.status === 200 || response.data?.status === 200) {
          toast.success('Store updated successfully!', {
            position: toast.POSITION.TOP_RIGHT,
          });
          setSidebarOpen(false);
          resetForm();
          setIsEditMode(false);
          setEditingStore(null);
          fetchMyStores();
        } else {
          toast.error('Failed to update store', {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      } else {
        // Create mode
        // Check if user already has a store
        // const checkResponse = await checkStore();
        // if (!checkResponse.data?.success) {
        //   toast.error('You already have a store');
        //   return;
        // }

        const formData = new FormData();
        formData.append('storeName', storeName);
        formData.append('store_category_id', selectedCategory?.value || '');
        formData.append('storeAddress', storeAddress || '');
        formData.append('storeDescription', shortDescription);
        if (logoFile) formData.append('File', logoFile);
        if (docFile) formData.append('File2', docFile);
        formData.append('chainId', selectedChain);
        formData.append('symbol', selectedToken?.value || '');
        formData.append('store_type', selectedType);
        formData.append('delivery_available_countries', JSON.stringify((selectedCountries || []).map(c => {
          if (typeof c === 'string') return c;
          return c?.value || c;
        })));
        formData.append('online_delivery_available', onlineDelivery ? 'true' : 'false');

        let response;
        if (beneficiaryToggle === 'external') {
          formData.append('store_creation_fee', 2);
          response = await createMarketPlaceExternal(formData);
        } else {
          response = await createMarketPlaceInternal(formData);
        }

        if (response.status === 200 || response.data?.status === 200) {
          toast.success('Store created successfully!', {
            position: toast.POSITION.TOP_RIGHT,
          });
          setSidebarOpen(false);
          resetForm();
          fetchMyStores();
        } else {
          toast.error('Failed to create store', {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} store:`, error);
      toast.error(error.response?.data?.Message || `Failed to ${isEditMode ? 'update' : 'create'} store`, {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteStore = async () => {
    if (!storeToDelete) return;

    try {
      setIsLoading(true);
      const payload = {
        storeId: storeToDelete._id
      }

      const response = await deleteMyStore(payload);

      if (response.status === 200 || response.data?.status === 200) {
        toast.success('Store deleted successfully', {
          position: toast.POSITION.TOP_RIGHT,
        });
        setStoreToDelete(null);
        // Refresh stores list
        fetchMyStores();
      } else {
        toast.error('Failed to delete store', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      console.error('Error deleting store:', error);
      toast.error(error.response?.data?.Message || 'Failed to delete store', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      {isLoading && (
        <LoadingOverlay>
          <Ring size={65} color="#5ce9ff" />
        </LoadingOverlay>
      )}

      {/* Header */}
      <HeaderSection>
        <div>
          <HeaderTitle>My Stores</HeaderTitle>
          <HeaderSubtitle>Manage your stores on PharmaCrowd Marketplace</HeaderSubtitle>
        </div>
        <NewStoreButton onClick={openNew}>
          + New Store
        </NewStoreButton>
      </HeaderSection>

      {/* Store Grid */}
      {isLoadingStores ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Ring size={65} color="#5ce9ff" />
        </div>
      ) : myStores.length > 0 ? (
        <StoreGrid>
          {myStores.map((store) => (
            <StoreCard key={store._id}>
              <StoreImageSection>
                <StoreLogo
                  src={store.storeLogo || '/img/default-store.png'}
                  alt={store.storeName}
                  onError={(e) => {
                    e.target.src = '/img/default-store.png';
                  }}
                />
              </StoreImageSection>
              <StoreContentSection>
                <StoreHeader>
                  <StoreName>{store.storeName}</StoreName>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {store.status && (
                      <StatusBadge status={store.status}>{store.status}</StatusBadge>
                    )}
                    <StoreTag>{store.store_type || 'standard'}</StoreTag>
                    {store.category?.category_name && (
                      <CategoryBadge>{store.category.category_name}</CategoryBadge>
                    )}
                  </div>
                </StoreHeader>
                {store.storeDescription && (
                  <StoreDescription>
                    {store.storeDescription}
                  </StoreDescription>
                )}
                <StoreActions>
                  <ViewStoreButton
                    onClick={(e) => {
                      e.stopPropagation();
                      history.push(`/storedetail/${store._id}`);
                    }}
                  >
                    View Store
                  </ViewStoreButton>
                  <ActionLinks>
                    <EditLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openEdit(store);
                      }}
                    >
                      Edit
                    </EditLink>
                    <DeleteLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setStoreToDelete(store);
                      }}
                    >
                      Delete
                    </DeleteLink>
                  </ActionLinks>
                </StoreActions>
              </StoreContentSection>
            </StoreCard>
          ))}
        </StoreGrid>
      ) : (
        <EmptyState>
          <EmptyStateTitle>No Stores Yet</EmptyStateTitle>
          <EmptyStateText>Create your first store to get started on the marketplace</EmptyStateText>
          <NewStoreButton onClick={openNew}>
            + Create Your First Store
          </NewStoreButton>
        </EmptyState>
      )}

      {/* Sidebar Editor - matching the provided design */}
      {sidebarOpen && (
        <SidebarContainer isOpen={sidebarOpen}>
          <SidebarTitle>{isEditMode ? 'Edit Store' : 'Store Editor'}</SidebarTitle>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <FormGroup>
              <Label>Store name</Label>
              <Input
                type="text"
                placeholder="Store name"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Slug</Label>
              <Input
                type="text"
                placeholder="Slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Short description</Label>
              <TextArea
                placeholder="Short description"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                rows={3}
              />
            </FormGroup>

            <FormGroup>
              <Label>Store Address</Label>
              <Input
                type="text"
                placeholder="Store address"
                value={storeAddress}
                onChange={(e) => setStoreAddress(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Category</Label>
              <SelectWrapper>
                <Select
                  options={categories}
                  value={selectedCategory || null}
                  onChange={(option) => setSelectedCategory(option)}
                  placeholder="Select category"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                />
              </SelectWrapper>
            </FormGroup>

            {/* Store Images */}
            <FormGroup>
              <Label>Store Images</Label>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
                Max {MAX_IMAGE_SIZE / (1024 * 1024)}MB. Allowed: JPG, JPEG, PNG, GIF, WEBP
              </div>
              <FileUploadButton
                type="button"
                onClick={() => imagesInputRef.current?.click()}
                disabled={images.length >= MAX_IMAGES}
              >
                {images.length >= MAX_IMAGES ? 'Image Uploaded' : 'Upload Image'}
              </FileUploadButton>
              <FileInput
                ref={imagesInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
              />
              <ImageGrid>
                {images.map((img, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <ImagePreview
                      src={img}
                      alt={`Store ${i + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImages([]);
                        if (imagesInputRef.current) {
                          imagesInputRef.current.value = '';
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        background: '#ff3b6b',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </ImageGrid>
            </FormGroup>

            {/* Traceability Documents */}
            <FormGroup>
              <Label>Traceability Documents</Label>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
                Max {MAX_DOCUMENT_SIZE / (1024 * 1024)}MB. Allowed: PDF, DOC, DOCX
              </div>
              <FileUploadButton
                type="button"
                onClick={() => documentInputRef.current?.click()}
                disabled={documents.length >= MAX_DOCUMENTS}
              >
                {documents.length >= MAX_DOCUMENTS ? 'Document Uploaded' : 'Upload Document'}
              </FileUploadButton>
              <FileInput
                ref={documentInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleDocumentUpload}
              />
              <DocumentList>
                {documents.map((d, i) => (
                  <li key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{d.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setDocuments([]);
                        if (documentInputRef.current) {
                          documentInputRef.current.value = '';
                        }
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#ff3b6b',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '0 4px',
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </DocumentList>
            </FormGroup>

            <FormGroup>
              <Label>Role</Label>
              <SelectStyled
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="standard">Standard</option>
                <option value="enterprise">Enterprise</option>
                <option value="pharma">Pharma</option>
              </SelectStyled>
            </FormGroup>

            <FormGroup>
              <Label>Store Type</Label>
              <SelectWrapper>
                <Select
                  options={storeTypeOptions}
                  value={storeTypeOptions.find(t => t.value === selectedType) || null}
                  onChange={(option) => {
                    if (option) {
                      setSelectedType(option.value);
                    }
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </SelectWrapper>
            </FormGroup>

            <FormGroup>
              <Label>Symbol</Label>
              <SelectWrapper>
                <Select
                  options={symbolOptions}
                  value={selectedToken}
                  onChange={(option) => setSelectedToken(option)}
                  placeholder="Select symbol"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                />
              </SelectWrapper>
            </FormGroup>

            <FormGroup>
              <Label>Delivery Available Countries</Label>
              <SelectWrapper>
                <Select
                  isMulti
                  options={countryOptions}
                  value={selectedCountries || []}
                  onChange={(options) => {
                    try {
                      setSelectedCountries(options || []);
                    } catch (error) {
                      console.error('Error updating countries:', error);
                      toast.error('Error updating countries selection');
                    }
                  }}
                  placeholder="Select countries"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  components={makeAnimated()}
                  isSearchable
                  filterOption={(option, searchText) => {
                    if (!searchText) return true;
                    const label = option.label || '';
                    return label.toLowerCase().includes(searchText.toLowerCase());
                  }}
                  noOptionsMessage={() => 'No countries found'}
                />
              </SelectWrapper>
            </FormGroup>

            <FormGroup>
              <Label>Status</Label>
              <SelectStyled
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </SelectStyled>
            </FormGroup>
          </div>

          <ButtonGroup>
            <SaveButton onClick={handleSave} disabled={isLoading}>
              {isLoading ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update' : 'Save')}
            </SaveButton>
            <CancelButton onClick={() => {
              setSidebarOpen(false);
              resetForm();
              setIsEditMode(false);
              setEditingStore(null);
            }}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </SidebarContainer>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        store={storeToDelete}
        onConfirm={handleDeleteStore}
        onCancel={() => setStoreToDelete(null)}
      />
    </PageContainer>
  );
}
